// js/editor-app.mjs
// Bootstrap: fetch calibration, wire modules, build the palette. All mutations go
// through app.mutate() so undo snapshots and re-render stay consistent.
import { buildCatalog } from './kaju-editor-geometry.js?v=e343a13f4f';
import { EditorState } from './kaju-editor-state.js?v=e343a13f4f';
import { History } from './kaju-editor-history.js?v=e343a13f4f';
import { EditorRenderer } from './kaju-editor-renderer.js?v=e343a13f4f';
import { Camera } from './kaju-editor-camera.js?v=e343a13f4f';
import { Interaction } from './kaju-editor-interaction.js?v=e343a13f4f';
import { analyzeSculpture, buildCollapseChain, groundYOf } from './kaju-editor-statics-bridge.js?v=e343a13f4f';
import { MARBLE, buildMarbleWorld, stepMarble, tiltVector, fixedTiltAccel } from './kaju-editor-marble.js?v=e343a13f4f';
import { computeVermitteln } from './kaju-editor-vermitteln.js?v=e343a13f4f';
import { loadExamples, examplePreviewSvg } from './kaju-editor-examples.js?v=e343a13f4f';

// Data/skin locations are overridable so the same modules can run from the local dev
// server (defaults, relative to /stein-editor/) and from a host that keeps every file in
// one flat directory (Shopify theme assets set window.KAJU_EDITOR_PATHS before boot).
const PATHS = globalThis.KAJU_EDITOR_PATHS ?? {};

let cal;
try {
  const res = await fetch(PATHS.calibration ?? '../stein-kompositor/render/calibration.json');
  if (!res.ok) throw new Error('HTTP ' + res.status);
  cal = await res.json();
} catch (err) {
  document.body.innerHTML = '<div style="padding:24px;font-family:system-ui;color:#532c29"><strong>Editor konnte nicht laden.</strong><br>Kalibrierung nicht erreichbar (' + err.message + '). Bitte Seite neu laden.</div>';
  throw err;
}
const catalog = buildCatalog(cal);
// Templates are a nice-to-have: a broken or missing file must never keep the editor or
// the user's own saves from working, so this failure stays local to the dialog.
let templates = [], templateError = null;
try {
  const loaded = await loadExamples(PATHS.examples ?? 'data/example-sculptures.json', catalog);
  templates = loaded.templates;
  if (loaded.rejected.length) console.warn('Ungültige Vorlagen übersprungen:', loaded.rejected);
} catch (err) {
  templateError = err.message;
  console.warn('Vorlagen nicht geladen:', err);
}
const svg = document.getElementById('stage');

const state = new EditorState(catalog);
const history = new History(50);
const renderer = new EditorRenderer(svg, catalog,
  PATHS.assetBase ? { assetBase: PATHS.assetBase } : {});
const camera = new Camera(svg);
const STORAGE_KEY = 'kaju-stein-editor:sculptures';
const cloneDoc = (doc) => typeof structuredClone === 'function'
  ? structuredClone(doc)
  : JSON.parse(JSON.stringify(doc));

const app = {
  state, history, renderer, camera, catalog,
  mutate(fn) {
    history.record(state.doc);
    fn(state);
    renderer.syncAll(state.doc);
    this.onChange?.();
  },
  undo() { const d = history.undo(state.doc); if (d) { state.doc = d; renderer.syncAll(d); this.onChange?.(); } },
  redo() { const d = history.redo(state.doc); if (d) { state.doc = d; renderer.syncAll(d); this.onChange?.(); } },
};

// Zwei Leisten (Kai, 2026-08-23): oben die Spezialsteine (weiche/weiche-gelb), unten die
// vier normalen Bogensteine -- fix in Bildschirmbreite, kein Scrollen. Hand/Quick-Bau sind
// keine Kacheln mehr; sie sitzen als Menüpunkte hinter dem ⋮-Menü (siehe btn-tool-* unten).
const paletteTopEl = document.getElementById('palette-top');
const paletteEl = document.getElementById('palette');
const addTile = (target, type) => {
  const tile = document.createElement('button');
  tile.className = 'tile';
  tile.dataset.type = type;
  tile.innerHTML = renderer.tileSVG(type, 76);
  tile.setAttribute('aria-label', type);
  target.appendChild(tile);
};
for (const type of ['weiche', 'weiche-gelb']) addTile(paletteTopEl, type);
for (const type of ['gelb', 'orange', 'gruen', 'wuerfel']) addTile(paletteEl, type);

const interaction = new Interaction(app, { stage: svg, palette: [paletteTopEl, paletteEl] });
interaction.refreshPalette();
const toolHandBtn = document.getElementById('btn-tool-hand');
const toolBuildBtn = document.getElementById('btn-tool-build');
function refreshToolButtons() {
  toolHandBtn.setAttribute('aria-pressed', String(interaction.tool === 'hand'));
  toolBuildBtn.setAttribute('aria-pressed', String(interaction.tool === 'build'));
}
toolHandBtn.addEventListener('click', () => { interaction.setTool('hand'); refreshToolButtons(); });
toolBuildBtn.addEventListener('click', () => { interaction.setTool('build'); refreshToolButtons(); });
refreshToolButtons();
camera.onTwistStart = () => interaction.beginTwist();
camera.onTwist = (deg) => interaction.twistBy(deg);
camera.onTwistEnd = () => interaction.endTwist();

const docNameEl = document.getElementById('doc-name');
const loadDialog = document.getElementById('load-dialog');
const saveDialog = document.getElementById('save-dialog');
const saveNameInput = document.getElementById('save-name');
const saveConfirmBtn = document.getElementById('save-confirm');
const loadList = document.getElementById('load-list');
const templateList = document.getElementById('template-list');
const importInput = document.getElementById('import-file');
const zoomSlider = document.getElementById('zoom-slider');
const moreBtn = document.getElementById('btn-more');
const moreMenu = document.getElementById('more-menu');
const modeToggle = document.getElementById('mode-toggle');
const simBar = document.getElementById('sim-bar');
const verdictEl = document.getElementById('verdict');

// Tool mode: 'legebild' (free building, today's behaviour), 'bogen' (Bogensimulator:
// elevation with a Baugrund line + gravity + statics verdict), 'murmel' (top-down,
// stones = static Banden, tilt-controlled marble), 'vermitteln' (render-only overlay that
// spreads a residual gap over the joints of both chains). Kai's decisions.
const MODES = ['legebild', 'bogen', 'murmel', 'vermitteln'];
let mode = 'legebild';
app.mode = mode;
let collapseRAF = 0;

function refreshGround() {
  if (mode === 'bogen') renderer.showGround(groundYOf(state, catalog));
  else renderer.hideGround();
}
function stopCollapse() {
  if (!collapseRAF && !renderer._base) return;
  if (collapseRAF) { cancelAnimationFrame(collapseRAF); collapseRAF = 0; }
  renderer.endCollapse();
  renderer.syncAll(state.doc);                  // snap the structure back (preview)
}
function clearVerdict() { verdictEl.hidden = true; verdictEl.className = ''; renderer.hidePressure(); }

function setMode(next) {
  if (!MODES.includes(next)) return;
  const prev = mode;
  mode = next; app.mode = next;
  for (const b of modeToggle.querySelectorAll('button')) {
    const on = b.dataset.mode === next;
    b.classList.toggle('active', on);
    b.setAttribute('aria-selected', String(on));
  }
  simBar.hidden = next !== 'bogen';
  document.body.classList.toggle('murmel-mode', next === 'murmel');
  document.body.classList.toggle('vermitteln-mode', next === 'vermitteln');
  stopCollapse(); clearVerdict();               // every mode starts clean -- no premature/stale verdict
  if (next === 'murmel') enterMarbleMode();
  else if (prev === 'murmel') exitMarbleMode();
  if (next === 'vermitteln') enterVermittelnMode();
  else if (prev === 'vermitteln') exitVermittelnMode();
  refreshGround();
}

const VERDICT = {
  stable:   { label: 'Trägt.',   cls: 'ok' },
  critical: { label: 'Kritisch', cls: 'warn' },
  unstable: { label: 'Rumms.',   cls: 'bad' },
  empty:    { label: 'Leer',     cls: 'muted' },
  error:    { label: 'Fehler',   cls: 'muted' },
};
function showVerdict(label, cls) { verdictEl.hidden = false; verdictEl.className = cls; verdictEl.textContent = label; }

// One "Physik aktivieren" action (merges the old Testen + Fallen-lassen buttons):
// solve the statics, show the verdict + pressure; if the structure is unstable, run
// the collapse dynamics so you watch it fall. Mirrors the native Bauprobe flow
// (stable/critical -> stands, unstable -> animate the collapse).
function runPhysics() {
  stopCollapse();
  const internals = window.KajuBauprobe?._internals;
  if (!internals) { showVerdict('Solver nicht geladen', 'muted'); return; }
  if (!state.doc.stones.length) { showVerdict(VERDICT.empty.label, VERDICT.empty.cls); return; }
  const r = analyzeSculpture(internals, state, catalog, groundYOf(state, catalog));
  if (r.status === 'unstable') { runCollapse(); return; }
  renderer.showPressure(r.pressurePoints);
  const v = VERDICT[r.status] || VERDICT.error;
  showVerdict(v.label, v.cls);
}

// Collapse preview: run the dynamics, animate the falling stones, then snap back.
function runCollapse() {
  const internals = window.KajuBauprobe?._internals;
  if (!internals) { showVerdict('Solver nicht geladen', 'muted'); return; }
  if (!state.doc.stones.length) return;
  stopCollapse();
  const gy = groundYOf(state, catalog);
  const { chain, starts } = buildCollapseChain(state, catalog, gy);
  const world = internals.createCollapseWorld(chain);
  const stones = state.doc.stones.slice();       // freeze order (world.bodies aligns by index)
  renderer.hidePressure();
  renderer.beginCollapse();
  let frames = 0;
  const tick = () => {
    for (let k = 0; k < 2; k++) internals.stepWorld(world);   // ~120 Hz at 60 fps
    for (let i = 0; i < world.bodies.length; i++) {
      const b = world.bodies[i];
      renderer.setCollapseTransform(stones[i].id, starts[i], [b.pos[0], gy - b.pos[1]], -(b.ang * 180 / Math.PI));
    }
    if (!world.settled && ++frames < 60 * 13) { collapseRAF = requestAnimationFrame(tick); return; }
    collapseRAF = 0;
    showVerdict(world.outcome === 'wedged' ? 'Verkeilt.' : 'Eingestürzt.', world.outcome === 'wedged' ? 'warn' : 'bad');
    setTimeout(() => { renderer.endCollapse(); renderer.syncAll(state.doc); }, 1600);  // snap back
  };
  collapseRAF = requestAnimationFrame(tick);
}

// ---- Murmel mode --------------------------------------------------------------
// Top-down toy: tap the field to drop the marble, stones are static Banden, the
// marble accelerates in the device-tilt direction (murmel-mode-handoff.md). No goal,
// no win/lose. Editing is disabled (Interaction ignores palette/stage in this mode).
const marbleBar = document.getElementById('marble-bar');
const tiltBtn = document.getElementById('btn-tilt');
const tiltZeroBtn = document.getElementById('btn-tilt-zero');
const tiltFixedBtn = document.getElementById('btn-tilt-fixed');
const tiltModeGroup = document.getElementById('tilt-mode');
const fixedTiltWrap = document.getElementById('fixed-tilt');
const fixedTiltRange = document.getElementById('fixed-tilt-range');
const fixedTiltValue = document.getElementById('fixed-tilt-value');
Object.assign(fixedTiltRange, { min: MARBLE.fixedTiltMinDeg, max: MARBLE.fixedTiltMaxDeg, value: MARBLE.fixedTiltDeg });
fixedTiltValue.textContent = `${MARBLE.fixedTiltDeg}°`;
const marbleResetBtn = document.getElementById('btn-marble-reset');
const marbleHint = document.getElementById('marble-hint');

const marble = { world: null, raf: 0 };
// gx/gy = tilt acceleration in EDITOR screen frame (y-down), cm/s^2. `available`
// flips on the first real sensor event. `fixed` = "Feste Neigung": the marble rolls
// toward the screen bottom on a board pitched by `fixedDeg` (slider) instead of
// following the phone. It is on until the sensor goes live (that switches to
// Kippen once), and the player can switch back and forth afterwards. `last` keeps
// the newest raw pose so "Neigung nullen" can adopt it as the new flat.
const tilt = { listening: false, available: false, fixed: true, fixedDeg: MARBLE.fixedTiltDeg,
               zero: { beta: 0, gamma: 0 }, last: null, gx: 0, gy: 0 };

// SIGN CONVENTION (to be confirmed on Kai's iPhone, flip here if wrong): beta > zero
// = top edge pitched up -> marble rolls toward the bottom edge (+y screen); gamma >
// zero = right edge down -> rolls right (+x screen). Neutral is ABSOLUTE flat
// (zero = {0,0}): a phone lying flat on the table does not roll. "Neigung nullen"
// re-zeros to the current pose so a comfortable holding angle becomes flat instead.
function onOrientation(e) {
  if (e.beta == null || e.gamma == null) return;
  if (!tilt.available) { tilt.available = true; tilt.fixed = false; syncTiltControls(); }
  tilt.last = { beta: e.beta, gamma: e.gamma };
  let { gx, gy } = tiltVector(e, tilt.zero);
  const ang = screen.orientation?.angle ?? window.orientation ?? 0;   // remap for landscape
  if (ang === 90) [gx, gy] = [gy, -gx];
  else if (ang === 270 || ang === -90) [gx, gy] = [-gy, gx];
  else if (ang === 180) [gx, gy] = [-gx, -gy];
  tilt.gx = gx; tilt.gy = gy;
}

function startTiltListener() {
  if (tilt.listening) return;
  tilt.listening = true;
  window.addEventListener('deviceorientation', onOrientation);
}

// re-zero to the current pose: "das halte ich jetzt als flach"
function zeroTilt() {
  if (tilt.last) tilt.zero = { ...tilt.last };
}

// iOS 13+ requires requestPermission() from a user tap -> the "Kippen aktivieren"
// button. Elsewhere the listener just starts; if no sensor event ever arrives the
// fallback stays active (tilt.available never flips).
async function enableTilt() {
  try {
    const res = await DeviceOrientationEvent.requestPermission();
    if (res !== 'granted') return;                   // fixed incline stays active
    startTiltListener();
    syncTiltControls();
  } catch { /* keep the button; fixed incline stays active */ }
}

const needsTiltPermission = () =>
  typeof DeviceOrientationEvent !== 'undefined' &&
  typeof DeviceOrientationEvent.requestPermission === 'function';

// Fest|Kippen switch whenever a sensor is or can be had (on iOS "Kippen" asks for the
// permission); fixed mode shows the incline slider, tilt mode "Nullen" in its place.
// No sensor at all (desktop): no switch, slider only.
function syncTiltControls() {
  const canTilt = tilt.available || (needsTiltPermission() && !tilt.listening);
  tiltModeGroup.hidden = !canTilt;
  tiltFixedBtn.setAttribute('aria-pressed', String(tilt.fixed));
  tiltBtn.setAttribute('aria-pressed', String(!tilt.fixed));
  fixedTiltWrap.hidden = !tilt.fixed;
  tiltZeroBtn.hidden = tilt.fixed;
}

function setFixedTilt(fixed) {
  tilt.fixed = fixed;
  syncTiltControls();
}

// the play field = the visible stage area at drop time (soft Banden just outside); the
// zoom column covers the stage's right edge, so the right wall stops at its left side
const zoomCol = document.getElementById('zoom-col');
function viewField() {
  const w = Math.max(1, svg.clientWidth) / camera.zoom;
  const h = Math.max(1, svg.clientHeight) / camera.zoom;
  const x1 = camera.cx + w / 2 - zoomCol.offsetWidth / camera.zoom;
  return { x0: camera.cx - w / 2, y0: camera.cy - h / 2, x1, y1: camera.cy + h / 2 };
}

function stopMarbleLoop() {
  if (marble.raf) { cancelAnimationFrame(marble.raf); marble.raf = 0; }
  marble.world = null;
}

function resetMarble() {
  stopMarbleLoop();
  renderer.hideMarble();
  renderer.hideField();
  marbleHint.hidden = false;
  marbleResetBtn.disabled = true;
}

function dropMarble(pt) {
  const field = viewField();
  const world = buildMarbleWorld(state, catalog, pt, field);
  if (!world) return;                                // tap deep inside the stones: no room
  stopMarbleLoop();
  marble.world = world;
  renderer.showField(field);
  renderer.showMarble(marble.world.pos[0], marble.world.pos[1], MARBLE.radiusCm);
  marbleHint.hidden = true;
  marbleResetBtn.disabled = false;
  const tick = () => {
    if (!marble.world) return;
    const ax = tilt.fixed ? 0 : tilt.gx;
    const ay = tilt.fixed ? fixedTiltAccel(tilt.fixedDeg) : tilt.gy;   // schraeges Brett
    stepMarble(marble.world, ax, ay, 1 / 60);                // substeps inside, editor frame
    renderer.setMarble(marble.world.pos[0], marble.world.pos[1]);
    marble.raf = requestAnimationFrame(tick);
  };
  marble.raf = requestAnimationFrame(tick);
}

function enterMarbleMode() {
  interaction.select(null);                          // context bar off; stones are Banden now
  renderer.hideGhost();
  if (!needsTiltPermission()) startTiltListener();   // iOS waits for the Kippen tap
  syncTiltControls();
  marbleBar.hidden = false;
  marbleHint.hidden = false;
}

function exitMarbleMode() {
  resetMarble();
  marbleBar.hidden = true;
  marbleHint.hidden = true;
}

// Marble mode owns single-finger stage gestures: drag = pan, clean tap = drop the
// marble at the tap point. Two-finger stays with the camera (it fires pointercancel).
let marbleGesture = null;
svg.addEventListener('pointerdown', (e) => {
  if (mode !== 'murmel' || camera.pointers.size >= 2) return;
  marbleGesture = { id: e.pointerId, startPx: [e.clientX, e.clientY],
                    prevPx: [e.clientX, e.clientY], moved: false };
});
svg.addEventListener('pointermove', (e) => {
  const g = marbleGesture;
  if (mode !== 'murmel' || !g || e.pointerId !== g.id) return;
  if (camera.pointers.size >= 2) { marbleGesture = null; return; }
  if (!g.moved && Math.hypot(e.clientX - g.startPx[0], e.clientY - g.startPx[1]) < 8) return;
  g.moved = true;
  camera.cx -= (e.clientX - g.prevPx[0]) / camera.zoom;
  camera.cy -= (e.clientY - g.prevPx[1]) / camera.zoom;
  g.prevPx = [e.clientX, e.clientY];
  camera.apply();
});
svg.addEventListener('pointerup', (e) => {
  const g = marbleGesture;
  if (mode !== 'murmel' || !g || e.pointerId !== g.id) return;
  marbleGesture = null;
  if (!g.moved) dropMarble(camera.toWorld(e));
});
svg.addEventListener('pointercancel', () => { marbleGesture = null; });
tiltBtn.addEventListener('click', () => (tilt.available ? setFixedTilt(false) : enableTilt()));
tiltFixedBtn.addEventListener('click', () => setFixedTilt(true));
fixedTiltRange.addEventListener('input', () => {
  tilt.fixedDeg = Number(fixedTiltRange.value);
  fixedTiltValue.textContent = `${tilt.fixedDeg}°`;
});
tiltZeroBtn.addEventListener('click', () => zeroTilt());
marbleResetBtn.addEventListener('click', () => resetMarble());

// ---- Vermitteln mode ----------------------------------------------------------
// Two chains that were meant to meet keep a small residual gap; this mode spreads it over
// every joint of both chains -- purely in the rendering. The overlay map lives HERE and
// nowhere else: the document keeps its exact poses, so leaving the mode is one plain
// syncAll() with no overlay and needs no repair path (spec "Anwendung").
const vermittelnBar = document.getElementById('vermitteln-bar');
const vermittelnHint = document.getElementById('vermitteln-hint');

function applyVermitteln() {
  const { overlay, joints } = computeVermitteln(state);
  renderer.syncAll(state.doc, overlay);
  vermittelnBar.hidden = false;
  vermittelnHint.textContent = joints
    ? `${joints} ${joints === 1 ? 'Fuge' : 'Fugen'} vermittelt`
    : 'nichts zu vermitteln';                    // stays a normal mode, not an error state
}

function enterVermittelnMode() {
  interaction.select(null);                      // nothing is editable here; no context bar
  renderer.hideGhost();
  applyVermitteln();
}

function exitVermittelnMode() {
  vermittelnBar.hidden = true;
  renderer.syncAll(state.doc);                   // back to the exact poses, in one call
}

function readStoredDocs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeStoredDocs(store) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

function resetHistory() {
  history.past = [];
  history.future = [];
}

function syncDocName() {
  if (document.activeElement !== docNameEl)
    docNameEl.textContent = state.doc.name || 'Unbenannte Skulptur';
}

function commitDocName() {
  const next = docNameEl.textContent.trim() || 'Unbenannte Skulptur';
  if (next !== state.doc.name) app.mutate((s) => { s.doc.name = next; });
  syncDocName();
}

function loadDocument(doc) {
  state.doc = cloneDoc(doc);
  // The attachment graph is the truth, the stored poses are only a cache (EDITOR-SPEC §3).
  // Re-deriving them on load is what lets a sculpture saved against an older, less exact
  // catalog snap back onto the exact one instead of carrying its old gaps around forever.
  state.refreshPoses();
  resetHistory();
  renderer.setSelected(null);
  renderer.syncAll(state.doc);
  camera.fitAll(state, catalog);
  interaction._placedId = null;
  app.onChange?.();
}

function clearDocument() {
  // Immediate wipe (no confirm, Kai's choice) -- routed through app.mutate so it records
  // a history snapshot and stays undoable.
  if (!state.doc.stones.length) return;
  app.mutate((s) => { s.doc.stones = []; s.doc.rootStoneIds = []; });
  renderer.setSelected(null);
  interaction._placedId = null;
}

// Saving always asks for the name (Kai, 2026-10-23). The name decides the slot: an existing
// sculpture of that name is overwritten, any other name gets a new entry -- renaming and
// saving no longer silently replaces the sculpture the document was loaded from.
const nameKey = (name) => name.trim().toLocaleLowerCase('de-DE');

function findSlotByName(name) {
  const key = nameKey(name);
  const hits = Object.entries(readStoredDocs())
    .filter(([, item]) => nameKey(String(item?.name || item?.doc?.name || '')) === key)
    .sort((a, b) => String(b[1]?.updatedAt || '').localeCompare(String(a[1]?.updatedAt || '')));
  return hits[0]?.[0] ?? null;                   // older duplicates: the newest one wins
}

// Names of saves still waiting in the queue count as taken: confirming the same name again
// lands on that entry once it is written.
const pendingNames = new Map();                  // nameKey -> number of queued saves

function refreshSaveConfirm() {
  const name = saveNameInput.value.trim();
  saveConfirmBtn.disabled = !name;
  saveConfirmBtn.textContent = name && (pendingNames.has(nameKey(name)) || findSlotByName(name))
    ? 'Speichern und überschreiben' : 'Speichern und neu anlegen';
}

function openSaveDialog() {
  saveNameInput.value = state.doc.name || '';
  refreshSaveConfirm();
  saveDialog.showModal();
  saveNameInput.select();
}

// Saves run one after another in click order: the snapshot awaits skin images, and an
// earlier save finishing late must not overwrite a newer one.
let saveQueue = Promise.resolve();
function saveDocument(name) {
  state.doc.name = name;
  syncDocName();
  const now = new Date().toISOString();
  const doc = cloneDoc(state.doc);              // taken before the await: later edits stay out
  const key = nameKey(name);
  pendingNames.set(key, (pendingNames.get(key) ?? 0) + 1);
  saveQueue = saveQueue.then(() => {
    // The slot is picked in the queue, not at click time: an earlier save of the same name
    // may still be pending, and two quick saves must not become two entries. Queued saves
    // can start within one millisecond, hence the random suffix.
    doc.id = findSlotByName(name)
      ?? `sculpture_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    return writeSave(doc, now);
  }).catch((err) => {
    console.warn(err);
    alert('Speichern fehlgeschlagen: der Speicher im Browser ist voll.');
  }).finally(() => {
    const left = pendingNames.get(key) - 1;
    if (left) pendingNames.set(key, left); else pendingNames.delete(key);
    if (saveDialog.open) refreshSaveConfirm();
  });
}

async function writeSave(doc, now) {
  const thumb = await renderer.snapshot(doc);
  const store = readStoredDocs();
  const entry = { name: doc.name || 'Unbenannte Skulptur', updatedAt: now, doc };
  store[doc.id] = thumb ? { ...entry, thumb } : entry;
  try {
    writeStoredDocs(store);
  } catch {
    // A full localStorage must not cost the sculpture itself: give up the pictures (the
    // dialog falls back to outlines) before giving up the document.
    for (const item of Object.values(store)) delete item.thumb;
    writeStoredDocs(store);
  }
}

// Load-dialog tiles show only the picture (Kai, 2026-10-22); name and date stay reachable
// for screen readers and as a hover title.
function tileButton(className, label, picture) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = className;
  button.title = label;
  button.setAttribute('aria-label', label);
  if (typeof picture === 'string') button.innerHTML = picture;
  else button.appendChild(picture);
  return button;
}

// Template tiles show the same photo-skinned picture as saved sculptures. Drawn in the
// browser from the template's own document, once per page view; the outline stays as the
// instant placeholder and as the fallback if a picture cannot be drawn. One picture per
// task so the dialog paints first and stays responsive; failures are not remembered, the
// next opening simply tries again.
const templateThumbs = new Map();               // template id -> data URL
let thumbQueue = Promise.resolve();

function showPicture(button, url) {
  const img = document.createElement('img');
  img.alt = '';
  img.src = url;
  button.replaceChildren(img);
}

function renderTemplateList() {
  templateList.innerHTML = '';
  if (templateError) {
    templateList.innerHTML = '<p class="empty-list">Vorlagen konnten nicht geladen werden.</p>';
    return;
  }
  for (const tpl of templates) {
    const button = tileButton('template-entry', tpl.name, examplePreviewSvg(tpl.doc, catalog));
    button.dataset.templateId = tpl.id;
    templateList.appendChild(button);
    if (templateThumbs.has(tpl.id)) { showPicture(button, templateThumbs.get(tpl.id)); continue; }
    thumbQueue = thumbQueue.then(async () => {
      await new Promise((resolve) => setTimeout(resolve));
      if (!button.isConnected) return;          // dialog re-rendered meanwhile
      const url = templateThumbs.get(tpl.id) ?? await renderer.snapshot(tpl.doc).catch(() => null);
      if (!url) return;
      templateThumbs.set(tpl.id, url);
      if (button.isConnected) showPicture(button, url);
    });
  }
}

// A template is never edited in place: the working copy gets the generic local id; saving
// it goes to whatever slot the chosen name picks (see openSaveDialog).
function loadTemplate(tpl) {
  const doc = cloneDoc(tpl.doc);
  doc.id = 'sculpture_local';
  doc.createdAt = doc.updatedAt = new Date().toISOString();
  loadDocument(doc);
}

function openLoadDialog() {
  renderTemplateList();
  const store = readStoredDocs();
  const entries = Object.entries(store).sort((a, b) =>
    String(b[1]?.updatedAt || '').localeCompare(String(a[1]?.updatedAt || '')));
  loadList.innerHTML = entries.length ? '' : '<p class="empty-list">Noch nichts gespeichert.</p>';
  for (const [id, item] of entries) {
    const date = item.updatedAt ? new Date(item.updatedAt).toLocaleString('de-DE') : 'ohne Datum';
    const name = item.name || item.doc?.name || 'Unbenannte Skulptur';
    // Saves from before the snapshot existed have no thumb: draw their outline instead.
    let picture = item.doc?.stones?.length ? examplePreviewSvg(item.doc, catalog) : '';
    if (String(item.thumb).startsWith('data:image/')) {
      picture = document.createElement('img');      // src set as a property: never parsed as HTML
      picture.alt = '';
      picture.src = item.thumb;
    }
    const button = tileButton('load-entry', `${name} · ${date}`, picture);
    button.dataset.docId = id;
    loadList.appendChild(button);
  }
  loadDialog.hidden = false;
  loadDialog.showModal?.();
  if (!loadDialog.open) loadDialog.hidden = false;
}

function closeLoadDialog() {
  if (loadDialog.open) loadDialog.close();
  loadDialog.hidden = true;
}

function setMoreMenu(open) {
  moreMenu.hidden = !open;
  moreBtn.setAttribute('aria-expanded', String(open));
}

function slugName(name) {
  return (name || 'skulptur').toLowerCase().normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'skulptur';
}

function exportDocument() {
  const blob = new Blob([JSON.stringify(state.doc, null, 2)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${slugName(state.doc.name)}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 0);
}

async function importDocument(file) {
  try {
    const doc = JSON.parse(await file.text());
    if (doc?.schemaVersion !== 1 || !Array.isArray(doc.stones))
      throw new Error('Die Datei ist keine gültige Skulptur.');
    loadDocument(doc);
  } catch (err) {
    alert(`Import fehlgeschlagen: ${err.message}`);
  }
}

document.getElementById('btn-undo').addEventListener('click', () => app.undo());
document.getElementById('btn-redo').addEventListener('click', () => app.redo());
document.getElementById('btn-fit').addEventListener('click', () => camera.fitAll(state, catalog));
document.getElementById('btn-save').addEventListener('click', () => openSaveDialog());
saveNameInput.addEventListener('input', () => refreshSaveConfirm());
document.getElementById('save-cancel').addEventListener('click', () => saveDialog.close());
document.getElementById('save-form').addEventListener('submit', (e) => {
  e.preventDefault();
  const name = saveNameInput.value.trim();
  if (!name) return;
  saveDialog.close();
  saveDocument(name);
});
document.getElementById('btn-load').addEventListener('click', () => openLoadDialog());
document.getElementById('btn-export').addEventListener('click', () => exportDocument());
document.getElementById('btn-import').addEventListener('click', () => importInput.click());
const outlinesBtn = document.getElementById('btn-outlines');
outlinesBtn.addEventListener('click', () => {
  const on = outlinesBtn.getAttribute('aria-pressed') !== 'true';
  outlinesBtn.setAttribute('aria-pressed', String(on));
  renderer.setOutlines(on);
});
document.getElementById('btn-clear').addEventListener('click', () => clearDocument());
modeToggle.addEventListener('click', (e) => {
  const b = e.target.closest('button[data-mode]');
  if (b) setMode(b.dataset.mode);
});
document.getElementById('btn-physics').addEventListener('click', () => runPhysics());
document.getElementById('load-close').addEventListener('click', () => closeLoadDialog());
moreBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  setMoreMenu(moreMenu.hidden);
});
moreMenu.addEventListener('click', (e) => {
  if (e.target.closest('button')) setMoreMenu(false);
});
document.addEventListener('click', (e) => {
  if (!moreMenu.hidden && !moreMenu.contains(e.target) && e.target !== moreBtn) setMoreMenu(false);
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') setMoreMenu(false);
});
for (const evt of ['gesturestart', 'gesturechange', 'gestureend'])
  document.addEventListener(evt, (e) => e.preventDefault());
loadDialog.addEventListener('click', (e) => {
  const tplEntry = e.target.closest('.template-entry');
  if (tplEntry) {
    const tpl = templates.find((t) => t.id === tplEntry.dataset.templateId);
    if (tpl) {
      loadTemplate(tpl);
      closeLoadDialog();
    }
    return;
  }
  const entry = e.target.closest('.load-entry');
  if (!entry) return;
  const item = readStoredDocs()[entry.dataset.docId];
  if (item?.doc) {
    loadDocument(item.doc);
    closeLoadDialog();
  }
});
docNameEl.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') { e.preventDefault(); docNameEl.blur(); }
  if (e.key === 'Escape') { e.preventDefault(); syncDocName(); docNameEl.blur(); }
});
docNameEl.addEventListener('blur', () => commitDocName());
importInput.addEventListener('change', () => {
  const file = importInput.files?.[0];
  importInput.value = '';
  if (file) importDocument(file);
});
// The vertical fill-bar look (editor.css) has no native "value so far" pseudo-element in
// Blink/WebKit, so JS has to translate the raw value into a percentage the track gradient
// can read (--zoom-pct). min/max come from the input's own attributes, not a duplicated
// constant, so the two never drift apart.
function setZoomFill(zoom) {
  const min = +zoomSlider.min, max = +zoomSlider.max;
  zoomSlider.style.setProperty('--zoom-pct', `${((zoom - min) / (max - min)) * 100}%`);
}
// The bar is a plain HORIZONTAL range rotated -90deg (editor.css) -- -webkit-appearance:
// slider-vertical was not draggable on iOS, only tappable (Kai, 2026-08-23). Rotation
// doesn't touch layout size, so the input's pre-rotation width/height (= wrap height/width)
// has to be set from the actually rendered wrap box, not a fixed pixel guess -- otherwise it
// would not fill every viewport height.
const zoomWrap = document.getElementById('zoom-slider-wrap');
function layoutZoomSlider() {
  zoomSlider.style.width = `${zoomWrap.clientHeight}px`;
  zoomSlider.style.height = `${zoomWrap.clientWidth}px`;
}
layoutZoomSlider();
// The stage also changes size without a window resize (a mode swaps the Steinvorrat for its
// own bar), so re-measure on the stage itself: viewBox and the zoom bar follow at once.
new ResizeObserver(() => { camera.apply(); layoutZoomSlider(); }).observe(svg);
zoomSlider.addEventListener('input', (e) => { camera.set(camera.cx, camera.cy, +e.target.value); setZoomFill(+e.target.value); });
camera.onZoomChange = (zoom) => { zoomSlider.value = String(Math.round(zoom)); setZoomFill(zoom); };
zoomSlider.value = String(Math.round(camera.zoom));
setZoomFill(camera.zoom);

app.onChange = () => {
  document.getElementById('btn-undo').disabled = !history.canUndo();
  document.getElementById('btn-redo').disabled = !history.canRedo();
  syncDocName();
  interaction.refreshContextBar();
  if (collapseRAF || renderer._base) stopCollapse();   // structure changed -> abort collapse
  refreshGround();
  if (mode === 'bogen') clearVerdict();        // structure changed -> last verdict is stale
  if (mode === 'murmel' && marble.world) resetMarble();  // undo/redo -> marble world is stale
  // Building is locked in this mode, but undo/redo/Laden still change the document behind
  // it -- recompute so the overlay can never show a gap the sculpture no longer has.
  if (mode === 'vermitteln') applyVermitteln();
};
app.onChange();

window.__editor = app;          // hook for Playwright smoke tests
